import {
  Button,
  Check,
  Form,
  FormCard,
  FormItem,
  PageHeader,
  Select,
  TextArea,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import {
  ButtonsContainer,
  PageWrapper,
  DateAndTimeRowPicker
} from '../../components'
import { Form as AntdForm } from 'antd'
import DeleteIndividualAbsenceModal from './modals/DeleteIndividualAbsenceModal'
import { useState } from 'react'
import { getRequiredRule } from 'screens/Rostering/utils'
import { useNavigate, useParams } from 'react-router-dom'
import useUser from 'hooks/useUser/useUser'
import { Spinner } from 'components/generic'
import { showToast } from 'common/utils'
import { useAbsenceDetail } from 'screens/Rostering/hooks/useAbsenceDetail'
import { useAbsenceUpdate } from 'screens/Rostering/hooks/useAbsenceUpdate'
import { useAbsenceDelete } from 'screens/Rostering/hooks/useAbsenceDelete'
import { useAbsenceTypesList } from 'screens/Rostering/hooks/useAbsenceTypesList'
import { getDateTimeInZone } from 'screens/Rostering/utils.datetime'

const EditIndividualAbsenceScreen = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = AntdForm.useForm()
  const [isModalVisible, setIsModalVisible] = useState(false)

  const { absenceId } = useParams()
  const { absence, isAbsenceLoading } = useAbsenceDetail({ absenceId })
  const { updateAbsence, isUpdateLoading } = useAbsenceUpdate({
    absenceId,
    onUpdateSuccess: () => {
      showToast({ title: t('ABSENCE_EDITED') })
      navigate(-1)
    }
  })
  const { deleteAbsence, isDeleteLoading } = useAbsenceDelete({
    absenceId,
    onDeleteSuccess: () => {
      showToast({ title: t('ABSENCE_DELETED') })
      navigate(-2)
    }
  })
  const { user, isLoading: isUserLoading } = useUser({ id: absence?.userId })
  const { absenceTypes, isAbsenceTypesLoading } = useAbsenceTypesList()

  const absenceType = absenceTypes.find(
    (absenceType) => absenceType.id === absence?.absenceType
  )

  const createDateTimeValues = (dateString: string | undefined) => {
    if (!dateString) return { date: null, time: null }

    const dateTime = getDateTimeInZone(dateString)
    return {
      date: dateTime.utc(),
      time: dateTime.utc()
    }
  }

  const startDateTime = createDateTimeValues(absence?.startDate)
  const endDateTime = createDateTimeValues(absence?.endDate)

  const initialValues = {
    ...absence,
    user: `${user?.name.first} ${user?.name.last}`,
    'startDate.date': startDateTime.date,
    'startDate.time': startDateTime.time,
    'endDate.date': endDateTime.date,
    'endDate.time': endDateTime.time
  }

  const absenceTypesOptions = absenceTypes
    ?.filter((absenceType) => !absenceType.isDeleted)
    .map((absenceType) => ({
      value: absenceType.id,
      label: absenceType.name
    }))

  const handleOnFinish = async (values: any) => {
    const endDateValue = values['endDate.date']
    const endTimeValue = values['endDate.time']
    const endDate = endDateValue.format('YYYY-MM-DD')
    const endTime = endTimeValue.format('HH:mm:ss')
    const endDateWithTimezone = `${endDate}T${endTime}Z`

    const startDateValue = values['startDate.date']
    const startTimeValue = values['startDate.time']
    const startDate = startDateValue.format('YYYY-MM-DD')
    const startTime = startTimeValue.format('HH:mm:ss')
    const startDateWithTimezone = `${startDate}T${startTime}Z`

    try {
      await updateAbsence({
        absenceType: values.absenceType,
        userId: user?.id,
        startDate: startDateWithTimezone,
        endDate: endDateWithTimezone,
        absenceNotes: values?.absenceNotes,
        instancesUnallocated: 0
      })
    } catch (e: any) {
      showToast({
        title: e?.message,
        type: 'error'
      })
    }
  }

  if (isAbsenceLoading || isAbsenceTypesLoading || isUserLoading) {
    return <Spinner />
  }

  return (
    <>
      <PageHeader
        title={`${t('EDIT')} ${t('INDIVIDUAL_ABSENCE_TITLE', {
          user: `${user?.name.first} ${user?.name.last}`,
          type: absenceType?.name
        })}`}
        sticky
      >
        <ButtonsContainer>
          <Button onClick={() => navigate(-1)}>{t('CANCEL')}</Button>
          <Button variant="danger" onClick={() => setIsModalVisible(true)}>
            <Trash />
            {t('DELETE')}
          </Button>
          <Button
            isLoading={isUpdateLoading}
            variant="primary"
            onClick={form.submit}
          >
            <Check color={theme.color.neutral.color11} />
            {t('SAVE')}
          </Button>
        </ButtonsContainer>
      </PageHeader>
      <PageWrapper>
        <Form
          form={form}
          onFinish={handleOnFinish}
          initialValues={initialValues}
          onFinishFailed={() => {}}
        >
          <FormCard>
            <FormItem
              name="user"
              label={<Text>{t('TEAM_MEMBER')}</Text>}
              rules={[getRequiredRule(t('TEAM_MEMBER'))]}
            >
              <Select disabled />
            </FormItem>
            <FormItem
              name="absenceType"
              label={<Text>{t('ABSENCE_TYPE')}</Text>}
              rules={[getRequiredRule(t('ABSENCE_TYPE'))]}
            >
              <Select options={absenceTypesOptions} />
            </FormItem>
            <DateAndTimeRowPicker
              label={<Text>{t('START_DATE')}</Text>}
              name="startDate"
              required
            />
            <DateAndTimeRowPicker
              label={<Text>{t('END_DATE')}</Text>}
              name="endDate"
              required
              minDateField="startDate.date"
            />
          </FormCard>
          <FormCard title={t('ADDITIONAL_DETAILS')}>
            <FormItem name="absenceNotes" label={<Text>{t('NOTES')}</Text>}>
              <TextArea
                autoSize={{
                  minRows: 3
                }}
              />
            </FormItem>
          </FormCard>
        </Form>
      </PageWrapper>
      {isModalVisible && (
        <DeleteIndividualAbsenceModal
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          deleteAbsence={deleteAbsence}
          isDeleteLoading={isDeleteLoading}
          userName={`${user?.name.first} ${user?.name.last}`}
          absenceType={absenceType}
        />
      )}
    </>
  )
}

export default EditIndividualAbsenceScreen
